import { LanguageKeys } from 'src/common/constants/languages';
import { EOrder } from 'src/interfaces/Order';
import { PublishedFilter, matchesPublishedFilter } from 'src/helpers/publishedFilter';
import { ITrackMap } from 'src/interfaces/TrackMap';
import { TechnicalType } from 'src/interfaces/TechnicalType';

export type TrackMapSortBy = 'name' | 'fileName' | 'totalDistance' | 'distance';

type TrackMapFilterOptions = {
  query: string;
  distanceFilter: string | null;
  publishedFilter: PublishedFilter;
  sortBy: TrackMapSortBy;
  sortOrder: EOrder;
};

function matchesQuery(trackMap: ITrackMap, query: string, lang: LanguageKeys): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (trackMap.name.toLowerCase().includes(q)) return true;
  if (trackMap.fileName.toLowerCase().includes(q)) return true;
  const displayName = trackMap.i18n?.[lang] ?? '';
  if (displayName.toLowerCase().includes(q)) return true;
  const routeLabel = trackMap.route?.i18n?.[lang] ?? '';
  return routeLabel.toLowerCase().includes(q);
}

export function filterTrackMaps(
  trackMaps: ITrackMap[],
  distancesById: Record<string, TechnicalType>,
  lang: LanguageKeys,
  { query, distanceFilter, publishedFilter, sortBy, sortOrder }: TrackMapFilterOptions,
): ITrackMap[] {
  let result = trackMaps.filter(trackMap => {
    if (distanceFilter && trackMap.distance !== distanceFilter) return false;
    if (!matchesPublishedFilter(trackMap.published, publishedFilter)) return false;
    return matchesQuery(trackMap, query, lang);
  });

  result = [...result].sort((a, b) => {
    let cmp: number;
    switch (sortBy) {
      case 'fileName':
        cmp = a.fileName.localeCompare(b.fileName);
        break;
      case 'totalDistance':
        cmp = (a.totalDistance ?? 0) - (b.totalDistance ?? 0);
        break;
      case 'distance': {
        const aLabel = distancesById[a.distance]?.i18n[lang] ?? '';
        const bLabel = distancesById[b.distance]?.i18n[lang] ?? '';
        cmp = aLabel.localeCompare(bLabel);
        break;
      }
      case 'name':
      default:
        cmp = a.name.localeCompare(b.name);
        break;
    }
    return sortOrder === EOrder.desc ? -cmp : cmp;
  });

  return result;
}
